import { createSlice } from "@reduxjs/toolkit";
import { ActionType } from "../actionTypes";
import {
  Connector,
  ConnectorConfigUrlsMap,
  ConnectorStatusMap,
  ConnectorType,
  ConnectorUrlConfig,
} from "../types/pullConnector";
import {
  PushConnectorFormField,
  PushConnectorMap,
  initialPushConnectorForm,
} from "../types/pushConnector";
import { RegistrationMap } from "../types/marketplace";
import { Registration } from "../types/marketplace";

export interface ConnectorsState {
  connectors: Connector[];
  connectorTypes: ConnectorType[];
  connectorStatusMap: ConnectorStatusMap;
  connectorConfigUrls: ConnectorConfigUrlsMap;
  pushConnectors: PushConnectorMap;
  pushConnectorForm: PushConnectorFormField[];
  selectedPullConnectorTableId?: string;
  registrations: RegistrationMap;
}

const initialState: ConnectorsState = {
  connectors: [],
  connectorTypes: [],
  connectorStatusMap: {},
  connectorConfigUrls: {},
  pushConnectors: {},
  pushConnectorForm: initialPushConnectorForm,
  registrations: {},
};

export const connectorsSlice = createSlice({
  name: "connectors",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // SET_CONNECTORS
    builder.addCase(ActionType.SET_CONNECTORS, (state, action: any) => {
      return { ...state, connectors: action.connectors };
    });

    // SET_CONNECTOR_TYPES
    builder.addCase(ActionType.SET_CONNECTOR_TYPES, (state, action: any) => {
      return { ...state, connectorTypes: action.connectorTypes };
    });

    // SET_CONNECTOR_STATUS_MAP
    builder.addCase(
      ActionType.SET_CONNECTOR_STATUS_MAP,
      (state, action: any) => {
        return { ...state, connectorStatusMap: action.connectorStatusMap };
      },
    );

    // ADD_PULL_CONNECTOR_STATUS — merges a single connector's status into the map
    builder.addCase(
      ActionType.ADD_PULL_CONNECTOR_STATUS,
      (state, action: any) => {
        const { connectorId, status } = action;
        return {
          ...state,
          connectorStatusMap: { ...state.connectorStatusMap, [connectorId]: status },
        };
      },
    );

    // ADD_PULL_CONNECTOR_URL_CONFIGS — merges connector config URL entries
    builder.addCase(
      ActionType.ADD_PULL_CONNECTOR_URL_CONFIGS,
      (state, action: any) => {
        const configs: ConnectorUrlConfig[] = action.configs;
        const actionMap = Object.fromEntries(
          configs.map(({ "connection-id": connectorId, url }) => [
            connectorId,
            url,
          ]),
        );
        return {
          ...state,
          connectorConfigUrls: { ...state.connectorConfigUrls, ...actionMap },
        };
      },
    );

    // SET_PUSH_CONNECTORS
    builder.addCase(ActionType.SET_PUSH_CONNECTORS, (state, action: any) => {
      return { ...state, pushConnectors: action.pushConnectors };
    });

    // SET_PUSH_CONNECTOR_FORM_DATA
    builder.addCase(
      ActionType.SET_PUSH_CONNECTOR_FORM_DATA,
      (state, action: any) => {
        return { ...state, pushConnectorForm: action.pushConnectorForm };
      },
    );

    // SET_SELECTED_PULL_CONNECTOR_TABLE
    builder.addCase(
      ActionType.SET_SELECTED_PULL_CONNECTOR_TABLE,
      (state, action: any) => {
        return {
          ...state,
          selectedPullConnectorTableId: action.selectedPullConnectorTableId,
        };
      },
    );

    // ADD_REGISTRATIONS
    builder.addCase(ActionType.ADD_REGISTRATIONS, (state, action: any) => {
      const registrations: Registration[] = action.registrations;
      const regMap = Object.fromEntries(
        registrations.map((reg) => [reg["registration-id"], reg]),
      );
      return {
        ...state,
        registrations: { ...state.registrations, ...regMap },
      };
    });
  },
});

export default connectorsSlice.reducer;
